import { Languages, LogOut, Menu, Moon, Search, Sun, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Dropdown } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useLive } from '@/hooks/LiveProvider';
import { LOCALES, useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import { paths } from '@/router/paths';
import { useTheme } from '@/theme/ThemeProvider';

/** Zustand des Live-Streams als kompakter Indikator. */
function LiveBadge() {
  const { status } = useLive();
  const { t } = useI18n();
  const config = {
    live: { label: t('live.live'), text: 'text-ok', dot: 'bg-ok animate-live' },
    connecting: { label: t('live.connecting'), text: 'text-t2', dot: 'bg-t2 animate-live' },
    offline: { label: t('live.offline'), text: 'text-warn', dot: 'bg-warn' },
  }[status];

  return (
    <span
      className={cn(
        'hidden items-center gap-1.5 rounded-full border border-line px-2.5 py-1 text-[11px] font-bold sm:inline-flex',
        config.text,
      )}
      title={t('live.hint')}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
      {config.label}
    </span>
  );
}

export function Topbar({
  onOpenMobileNav,
  onToggleCollapse,
  onOpenPalette,
}: {
  onOpenMobileNav: () => void;
  onToggleCollapse: () => void;
  onOpenPalette: () => void;
}) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();

  const iconButton =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-line text-t1 hover:bg-bg-3';

  return (
    <header className="sticky top-0 z-30 flex flex-none items-center gap-2.5 border-b border-line bg-bg-1/80 px-3.5 py-3 backdrop-blur-md sm:gap-3.5 sm:px-6">
      <button onClick={onOpenMobileNav} aria-label={t('nav.openMenu')} className={cn(iconButton, 'lg:hidden')}>
        <Menu size={17} />
      </button>

      <button
        onClick={onToggleCollapse}
        aria-label={t('nav.toggleSidebar')}
        className={cn(iconButton, 'hidden lg:flex')}
      >
        <Menu size={17} />
      </button>

      <button
        onClick={onOpenPalette}
        className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-[11px] border border-line bg-bg-inset px-3 text-t1 sm:max-w-xs"
      >
        <Search size={15} className="shrink-0" />
        <span className="hidden truncate text-[12.5px] sm:inline">{t('common.search')}</span>
        <span className="ml-auto hidden shrink-0 rounded-md border border-line-2 px-1.5 py-0.5 text-[10px] font-bold text-t2 sm:inline">
          Strg K
        </span>
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2.5">
        <LiveBadge />

        {/* Sprachumschalter: Oberflaeche, Zahlen- und Datumsformate folgen der Wahl */}
        <Dropdown
          align="right"
          trigger={
            <button className={cn(iconButton, 'w-auto gap-1.5 px-2.5 text-[11.5px] font-bold')} aria-label={t('nav.language')}>
              <Languages size={15} />
              {LOCALES.find((entry) => entry.code === locale)?.short}
            </button>
          }
          items={LOCALES.map((entry) => ({
            label: `${entry.short} · ${entry.label}`,
            onClick: () => setLocale(entry.code),
          }))}
        />

        <button
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? t('nav.themeToLight') : t('nav.themeToDark')}
          className={iconButton}
        >
          {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
        </button>

        <Dropdown
          align="right"
          trigger={
            <button className="flex items-center gap-2 rounded-[10px] pr-1 pl-0.5 hover:bg-bg-3">
              <Avatar name={user?.name || user?.email || 'Konto'} size="sm" />
              <span className="hidden text-left leading-tight md:block">
                <span className="block max-w-[160px] truncate text-[12.5px] font-bold text-t0">
                  {user?.name || user?.email}
                </span>
                <span className="block text-[10.5px] text-t2">
                  {user?.role === 'ADMIN' ? t('account.roleAdmin') : t('account.roleCustomer')}
                </span>
              </span>
            </button>
          }
          items={[
            {
              label: t('nav.accountSecurity'),
              icon: <UserCircle size={15} />,
              onClick: () => navigate(paths.account),
            },
            { divider: true, label: '' },
            {
              label: t('nav.logout'),
              icon: <LogOut size={15} />,
              danger: true,
              onClick: () => {
                void logout().then(() => navigate(paths.login));
              },
            },
          ]}
        />
      </div>
    </header>
  );
}
